export interface DropdownPosition {
  top: number
  left: number
  width: number
  maxHeight: number
  placedAbove: boolean
}

interface GetDropdownPositionOptions {
  align?: 'left' | 'right'
  triggerRect: DOMRect
  desiredWidthPx?: number
  desiredMaxHeight?: number
  bottomOffsetPx: number
  gapPx?: number
  minUsableHeightPx?: number
}

const VIEWPORT_PADDING_PX = 8

/**
 * The width a content-sized dropdown grows to so no option label is cut off:
 * the width it has now, plus the most any label overflows its box. Labels that
 * fit add nothing, so once every label fits the width stops changing.
 *
 * Nothing here may grow along with the panel. The panel is resized to the
 * result and watched for size changes, so any input that tracks the panel's
 * own rendered width (the width itself, a stretched label's full box) feeds
 * the panel back into itself, and rounding at a fractional width (display
 * scaling) then ratchets it wider on every pass, without end.
 */
export function getContentSizedDropdownWidth({
  appliedWidth,
  minWidth,
  labels,
}: {
  /** The width last given to the panel, not its measured width. */
  appliedWidth: number
  /** The anchor's width, or any other floor. */
  minWidth: number
  /** Each label's content width (scrollWidth) and the width it shows (clientWidth). */
  labels: ReadonlyArray<{ contentWidth: number; visibleWidth: number }>
}): number {
  const overflow = labels.reduce(
    (widest, label) => Math.max(widest, label.contentWidth - label.visibleWidth),
    0
  )
  return Math.max(Math.ceil(minWidth), Math.ceil(appliedWidth + overflow))
}

export function getViewportAwareDropdownHorizontalPosition({
  triggerRect,
  desiredWidthPx,
  align = 'left',
}: {
  triggerRect: Pick<DOMRect, 'left' | 'width'>
  desiredWidthPx?: number
  align?: 'left' | 'right'
}) {
  const viewportWidth = Math.max(0, window.innerWidth - VIEWPORT_PADDING_PX * 2)
  const width = Math.min(
    viewportWidth,
    Math.max(Math.round(triggerRect.width), Math.ceil(desiredWidthPx ?? triggerRect.width))
  )
  const left = Math.min(
    Math.max(
      VIEWPORT_PADDING_PX,
      Math.round(
        align === 'right' ? triggerRect.left + triggerRect.width - width : triggerRect.left
      )
    ),
    Math.max(VIEWPORT_PADDING_PX, window.innerWidth - VIEWPORT_PADDING_PX - width)
  )

  return { left, width }
}

export function getViewportAwareDropdownPosition({
  triggerRect,
  align,
  desiredWidthPx,
  desiredMaxHeight,
  bottomOffsetPx,
  gapPx = 4,
}: GetDropdownPositionOptions): DropdownPosition {
  const { left, width } = getViewportAwareDropdownHorizontalPosition({
    triggerRect,
    align,
    desiredWidthPx,
  })
  const spaceBelow = Math.max(
    0,
    Math.floor(window.innerHeight - triggerRect.bottom - gapPx - bottomOffsetPx)
  )
  const spaceAbove = Math.max(0, Math.floor(triggerRect.top - gapPx - VIEWPORT_PADDING_PX))
  const desiredHeight = desiredMaxHeight ?? Math.max(spaceBelow, spaceAbove)
  const belowFits = spaceBelow >= desiredHeight
  const aboveFits = spaceAbove >= desiredHeight
  const shouldPlaceAbove = !belowFits && (aboveFits || spaceAbove > spaceBelow)
  const availableHeight = shouldPlaceAbove ? spaceAbove : spaceBelow
  const maxHeight = Math.max(0, Math.min(desiredHeight, availableHeight))
  const top = shouldPlaceAbove
    ? Math.max(VIEWPORT_PADDING_PX, Math.round(triggerRect.top - gapPx - maxHeight))
    : Math.round(triggerRect.bottom + gapPx)

  return {
    top,
    left,
    width,
    maxHeight,
    placedAbove: shouldPlaceAbove,
  }
}
